import { DatePicker, Select, Tooltip } from 'antd';
import type { TimeRangePickerProps } from 'antd';
import { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import type { ISubscription } from '@leedpulse-monorepo/interfaces';

// Redéclaré ici plutôt qu'importé : le backoffice n'importe pas de code
// backend. Doit rester égal à MAX_PERIOD_DAYS dans
// backend/src/helpers/leedflow/overview-model.ts.
const MAX_PERIOD_DAYS = 90;

// Les bornes de période sont inclusives dans tout ce chantier : le front
// envoie toujours `from.startOf('day')` → `to.endOf('day')` (voir
// getOverviewStats.ts), donc une plage de N jours calendaires pèse
// `N * DAY_MS - 1` une fois normalisée, pas `N * DAY_MS`.
//
// Pour le préréglage "90 derniers jours", ça veut dire partir de -89 et non
// -90 : avec -90, `startOf('day')` d'il y a 90 jours à `endOf('day')`
// aujourd'hui couvre 90 jours pleins entre les deux minuits, PLUS la journée
// en cours jusqu'à 23:59:59.999, soit `90 * DAY_MS + (DAY_MS - 1)` =
// `91 * DAY_MS - 1` — un jour de trop, rejeté par le garde serveur
// (`period.to - period.from > MAX_PERIOD_DAYS * DAY_MS` dans
// `getOverviewStats` côté backend). Avec -89, ça donne
// `89 * DAY_MS + (DAY_MS - 1)` = `90 * DAY_MS - 1`, qui couvre bien 90 jours
// calendaires inclus et passe le contrôle serveur. Le libellé "90 derniers
// jours" reste correct : il décrit le nombre de jours couverts, pas l'offset
// utilisé pour l'obtenir. Le préréglage 60 applique le même -1, pour la même
// raison de justesse du libellé.
//
// Les préréglages 7/14/30 appliquent le même -1, pour deux raisons qui se
// rejoignent : le libellé (avec "-7", "7 derniers jours" couvrait 8 jours
// calendaires) et la comparaison. `previousPeriod`
// (backend/src/helpers/leedflow/overview-model.ts) recule d'une semaine pile
// tant que la sélection tient dans 7 jours, et compare des multiples de 7
// jours à leur homologue de la semaine passée — une sélection de 8 jours
// tombait dans la branche "période contiguë" et opposait une semaine aux 8
// jours d'avant, décalés d'un jour de semaine.
//
// Fonction, pas une constante figée : un `const` de niveau module n'est
// évalué qu'une fois, au chargement du bundle. Un onglet backoffice laissé
// ouvert à cheval sur minuit (cas normal d'un outil interne) verrait alors
// « Aujourd'hui » et « Hier » continuer de pointer sur le jour du chargement
// — inacceptable pour « Aujourd'hui », qui est le préréglage par défaut de la
// page. Les fenêtres glissantes « N derniers jours » y sont peu sensibles
// (elles se recalent d'elles-mêmes au fil des jours), mais les deux jours
// exacts ne le peuvent pas sans être recalculés. Appelée à chaque rendu.
const buildRangePresets = (): TimeRangePickerProps['presets'] => [
  { label: "Aujourd'hui", value: [dayjs().startOf('day'), dayjs().endOf('day')] },
  { label: 'Hier', value: [dayjs().add(-1, 'd').startOf('day'), dayjs().add(-1, 'd').endOf('day')] },
  { label: '7 derniers jours', value: [dayjs().add(-6, 'd'), dayjs()] },
  { label: '14 derniers jours', value: [dayjs().add(-13, 'd'), dayjs()] },
  { label: '30 derniers jours', value: [dayjs().add(-29, 'd'), dayjs()] },
  { label: '60 derniers jours', value: [dayjs().add(-59, 'd'), dayjs()] },
  { label: '90 derniers jours', value: [dayjs().add(-(MAX_PERIOD_DAYS - 1), 'd'), dayjs()] },
];

// Clés comprises par le backend (`OVERVIEW_EXCLUSIONS`,
// backend/src/helpers/leedflow/overview-model.ts), qui garde seul les ids de plan.
const EXCLUDE_OPTIONS = [{ value: 'internal', label: 'Comptes internes' }];

// Un libellé au-dessus de chaque filtre : le placeholder disparaît dès qu'une
// valeur est choisie, et un champ rempli ne dit plus ce qu'il filtre.
const LABEL_STYLE: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 2 };

interface OverviewFiltersProps {
  period: [Dayjs | null, Dayjs | null];
  onPeriodChange: (period: [Dayjs | null, Dayjs | null]) => void;
  subscriptionIds: string[];
  onSubscriptionIdsChange: (subscriptionIds: string[]) => void;
  excludes: string[];
  onExcludesChange: (excludes: string[]) => void;
  subscriptions: ISubscription[];
  loading: boolean;
}

const OverviewFilters: React.FC<OverviewFiltersProps> = ({
  period,
  onPeriodChange,
  subscriptionIds,
  onSubscriptionIdsChange,
  excludes,
  onExcludesChange,
  subscriptions,
  loading,
}) => {
  const rangePresets = buildRangePresets();

  // Quel préréglage décrit la période courante, s'il y en a un.
  //
  // Comparaison à la JOURNÉE, pas à la milliseconde : les préréglages sont
  // reconstruits à chaque rendu à partir de `dayjs()`, donc « 7 derniers
  // jours » se termine à l'instant présent alors que la période stockée se
  // termine à l'instant du clic. Une égalité stricte cesserait de matcher au
  // bout d'une seconde.
  const activePresetLabel = useMemo(() => {
    const [from, to] = period;
    if (!from || !to) return null;
    const match = (rangePresets ?? []).find((preset) => {
      const [presetFrom, presetTo] = preset.value as [Dayjs, Dayjs];
      return presetFrom.isSame(from, 'day') && presetTo.isSame(to, 'day');
    });
    return match ? String(match.label) : null;
  }, [period, rangePresets]);

  // Replié sur un préréglage, le champ affiche son nom au lieu de deux dates.
  // « 09/09/2026 → 09/09/2026 » ne dit pas « Aujourd'hui », il oblige à le
  // déduire. Le nom est porté par le premier des deux champs, le second est
  // vidé et le séparateur masqué : l'ensemble se lit comme une seule étiquette.
  // Sur une plage personnalisée, rien ne change — les dates sont alors la
  // seule description exacte de ce qui est affiché.
  const isPreset = activePresetLabel !== null;
  const displayFormat = isPreset
    ? (value: Dayjs) => (period[0] && value.isSame(period[0]) ? activePresetLabel : '')
    : 'DD/MM/YYYY';

  const subscriptionOptions = useMemo(
    () =>
      subscriptions.map((subscription) => ({
        value: subscription.id,
        // Le nom seul. La clé d'API tenait la moitié de la ligne et poussait
        // le nom hors du champ, alors que c'est lui qu'on cherche. Repli sur
        // la clé quand le nom est vide, pour ne jamais afficher une option
        // sans libellé.
        label: subscription.name || subscription.api_key,
      })),
    [subscriptions],
  );

  return (
    <>
      <div>
        <label htmlFor="overview-filter-period" style={LABEL_STYLE}>Période</label>
        <DatePicker.RangePicker
          id="overview-filter-period"
          placeholder={['Début', 'Fin']}
          format={displayFormat}
          separator={isPreset ? <span /> : undefined}
          style={{ width: isPreset ? 240 : 260 }}
          presets={rangePresets}
          value={period}
          disabledDate={(current) => {
            if (!current) return false;
            if (current.isAfter(dayjs(), 'day')) return true;
            return current.isBefore(dayjs().add(-(MAX_PERIOD_DAYS - 1), 'd'), 'day');
          }}
          onChange={(dates) => {
            if (dates) {
              const [beginDate, endDate] = dates;
              onPeriodChange([beginDate, endDate]);
            } else {
              onPeriodChange([null, null]);
            }
          }}
        />
      </div>

      <div>
        <label htmlFor="overview-filter-subscriptions" style={LABEL_STYLE}>Abonnements</label>
        <Select
          id="overview-filter-subscriptions"
          style={{ minWidth: 420 }}
          placeholder="Abonnements"
          allowClear
          mode="multiple"
          loading={loading}
          options={subscriptionOptions}
          value={subscriptionIds}
          onChange={(values) => onSubscriptionIdsChange(values)}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().trim().includes(input.toLowerCase().trim())
          }
        />
      </div>

      {/* Grisé dès qu'un abonnement est choisi : la sélection prime, l'exclusion
          ne sert qu'à la vue d'ensemble du parc. */}
      <div>
        <label htmlFor="overview-filter-exclude" style={LABEL_STYLE}>Exclure</label>
        <Tooltip title={subscriptionIds.length > 0 ? "Sans effet quand des abonnements sont choisis" : undefined}>
          <Select
            id="overview-filter-exclude"
            style={{ minWidth: 220 }}
            placeholder="Exclure"
            allowClear
            mode="multiple"
            options={EXCLUDE_OPTIONS}
            value={excludes}
            disabled={subscriptionIds.length > 0}
            onChange={(values) => onExcludesChange(values)}
          />
        </Tooltip>
      </div>
    </>
  );
};

export default OverviewFilters;
